import React, { useEffect, useRef, useState } from "react";

import { MAX_CONCURRENT_JOBS } from "./ingestJobs";
import { Modal, PrimaryButton, SecondaryButton } from "./shared";
import { TOP_UP_HREF, apiErrorCode, extractApiError } from "./useCatalogApi";

/**
 * Guard rail for the batch we send to the AI. Generous enough for a big JSON
 * paste of many products, but blocks an accidental "paste the whole file"
 * payload. The backend enforces its own (larger) hard cap on the route body.
 */
const MAX_INPUT_BYTES = 256 * 1024; // 256 KB of product text

const byteLen = (s: string): number =>
  typeof TextEncoder !== "undefined"
    ? new TextEncoder().encode(s).length
    : // Fallback: rough UTF-8 estimate.
      unescape(encodeURIComponent(s)).length;

const fmtKb = (bytes: number): string => `${(bytes / 1024).toFixed(1)} KB`;

interface StructuredRow {
  name: string;
  brand: string;
  upc: string;
  url: string;
}

const emptyRow = (): StructuredRow => ({
  name: "",
  brand: "",
  upc: "",
  url: "",
});

interface IngestTabProps {
  ingestChainId: string;
  /** Start a background research run. Rejects (IngestJobLimitError) if full. */
  onSubmit: (foodInput: string, productCount: number) => Promise<void>;
  /** How many runs are currently in flight (for the concurrency guard). */
  activeCount: number;
  /** Rough tokens per researched product, for the cost estimate. */
  tokensPerProduct: number;
  /** Passio staff aren't balance-checked; the estimate is just for reference. */
  isStaff?: boolean;
  /** Add this line to the list when `nonce` changes (e.g. "add a brand and retry"). */
  prefill?: { text: string; nonce: number };
}

const IngestTab: React.FC<IngestTabProps> = ({
  ingestChainId,
  onSubmit,
  activeCount,
  tokensPerProduct,
  isStaff,
  prefill,
}) => {
  const [freeText, setFreeText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [prefillHint, setPrefillHint] = useState("");
  const [rowsOpen, setRowsOpen] = useState(false);
  const [rows, setRows] = useState<StructuredRow[]>([emptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const [runError, setRunError] = useState<string>("");
  const [startedNote, setStartedNote] = useState<string>("");
  const [outOfTokens, setOutOfTokens] = useState(false);

  const atCapacity = activeCount >= MAX_CONCURRENT_JOBS;

  const updateRow = (i: number, patch: Partial<StructuredRow>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const nonEmptyRows = rows.filter(
    (r) => r.name.trim() || r.brand.trim() || r.upc.trim() || r.url.trim()
  );

  const freeTextCount = freeText
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean).length;
  const productCount = freeTextCount + nonEmptyRows.length;
  const estimate = Math.round(tokensPerProduct * productCount);

  useEffect(() => {
    const text = prefill?.text.trim();
    if (!text) return;
    setFreeText((prev) => {
      const lines = prev.split("\n").map((l) => l.trim());
      if (lines.includes(text)) return prev;
      return prev.trim() ? `${prev.replace(/\s+$/, "")}\n${text}` : text;
    });
    setStartedNote("");
    setPrefillHint(
      `We added “${text}” to your list. Put the brand in front of it (for example “Canyon Bakehouse ${text}”), or swap it for a UPC or product link, then click Research products.`
    );
    window.requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const at = el.value.lastIndexOf(text);
      if (at >= 0) el.setSelectionRange(at, at);
    });
  }, [prefill?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildFoodInput = (): string => {
    const parts: string[] = [];
    if (freeText.trim()) parts.push(freeText.trim());
    if (nonEmptyRows.length > 0) {
      parts.push(
        JSON.stringify(
          nonEmptyRows.map((r) => ({
            name: r.name.trim() || undefined,
            brand: r.brand.trim() || undefined,
            upc: r.upc.trim() || undefined,
            url: r.url.trim() || undefined,
          }))
        )
      );
    }
    return parts.join("\n");
  };

  const inputBytes = byteLen(buildFoodInput());
  const overLimit = inputBytes > MAX_INPUT_BYTES;

  const canRun =
    !submitting &&
    !overLimit &&
    !atCapacity &&
    (freeText.trim().length > 0 || nonEmptyRows.length > 0);

  const run = async () => {
    setError("");
    setRunError("");
    setStartedNote("");
    setOutOfTokens(false);
    const foodInput = buildFoodInput();
    if (!foodInput) {
      setError(
        "Add at least one product (free-form text or a structured row)."
      );
      return;
    }
    if (byteLen(foodInput) > MAX_INPUT_BYTES) {
      setError(
        `That's a lot of text (${fmtKb(
          byteLen(foodInput)
        )}). Keep it under ${fmtKb(
          MAX_INPUT_BYTES
        )} — split very large batches into a couple of runs.`
      );
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(foodInput, productCount);
      // Started in the background — clear the form so another batch can be
      // queued, and point the user at the runs tray.
      setFreeText("");
      setRows([emptyRow()]);
      setPrefillHint("");
      setStartedNote(
        "Research started — it runs in the background. Track progress in the Ingest runs panel (bottom-right); we'll surface the results there when it's done."
      );
    } catch (err) {
      const e = err as Error;
      if (e?.name === "RunCancelledError") {
        // The user backed out at the cost confirmation; keep their input.
      } else if (e?.name === "IngestJobLimitError") {
        setError(e.message);
      } else if (apiErrorCode(err) === "insufficient_tokens") {
        setOutOfTokens(true);
      } else {
        setRunError(extractApiError(err, "Research failed to start"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Step 1 — Add your products
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          List the specific products you want added, one per line. Give each one
          its <span className="font-medium">brand and product name</span>, a{" "}
          <span className="font-medium">UPC/barcode</span>, or a{" "}
          <span className="font-medium">product link</span>. We research the
          rest (nutrition, ingredients, country, tags) and check each one before
          you write anything.
        </p>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <div className="text-sm font-semibold text-amber-900">
          Specific products only — general foods are skipped
        </div>
        <p className="mt-1 text-sm text-amber-800">
          This tool adds the exact products you already have in mind. It
          can&apos;t search for products from a general description, so an entry
          like &ldquo;gluten free white bread&rdquo; gets skipped. Write
          &ldquo;Canyon Bakehouse Mountain White Gluten Free Bread&rdquo;
          instead, or paste its barcode or a link to it. Skipped entries can
          still use some tokens, so leave general foods off the list.
        </p>
        <div className="mt-2 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
          <div className="text-amber-900">
            <span className="font-semibold text-green-700">✓ Works:</span>{" "}
            Dave&apos;s Killer Bread 21 Whole Grains · a 12-digit barcode · a
            product page link
          </div>
          <div className="text-amber-900">
            <span className="font-semibold text-red-700">✕ Skipped:</span> white
            bread · protein bar · almond milk
          </div>
        </div>
      </div>

      {/* PRIMARY input: paste a list, one product per line. */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <label className="text-sm font-semibold text-gray-800">
          Paste your products
          <span className="ml-2 text-xs font-normal text-gray-400">
            one per line — brand + name, barcodes, or links (mix freely)
          </span>
        </label>
        {prefillHint && (
          <div className="mt-2 flex items-start justify-between gap-3 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
            <span>{prefillHint}</span>
            <button
              type="button"
              onClick={() => setPrefillHint("")}
              className="flex-shrink-0 text-indigo-400 hover:text-indigo-700"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          rows={8}
          disabled={submitting}
          placeholder={
            "MariGold Pure Joy Protein Bar\n" +
            "Keto Bars Chocolate Peanut Butter Keto Bar\n" +
            "DNX Grass-Fed Beef & Uncured Bacon Jalapeño Bar\n" +
            "850002251234\n" +
            "https://www.instacart.com/products/...\n" +
            '{"name":"Base Culture Keto Bread","brand":"Base Culture","upc":"..."}'
          }
          className="mt-2 w-full rounded-md border border-gray-300 p-3 font-mono text-sm focus:border-indigo-500 focus:outline-none"
        />
        <div className="mt-1.5 flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Include the brand on every line. You can also drop in a JSON array
            if you have structured data.
          </p>
          <span
            className={`text-xs tabular-nums ${
              overLimit ? "font-semibold text-red-600" : "text-gray-400"
            }`}
          >
            {fmtKb(inputBytes)} / {fmtKb(MAX_INPUT_BYTES)}
          </span>
        </div>
        {overLimit && (
          <p className="mt-1 text-xs font-medium text-red-600">
            This batch is too large to send. Trim it or split it into a couple
            of smaller runs.
          </p>
        )}
      </div>

      {/* OPTIONAL structured rows for when you want to be precise per field. */}
      {!rowsOpen ? (
        <button
          type="button"
          onClick={() => setRowsOpen(true)}
          className="text-sm font-medium text-indigo-600 underline decoration-dotted hover:text-indigo-800"
        >
          + Prefer separate fields? Add products as rows (name, brand, UPC,
          link)
        </button>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-gray-800">
              Add products as rows{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <span className="text-xs text-gray-400">
              Each row needs a name with its brand, a UPC, or a link
            </span>
          </div>

          {/* Column headers so it's clear what each field is and what's optional */}
          <div className="mt-3 hidden grid-cols-12 gap-2 px-1 text-xs font-medium text-gray-500 sm:grid">
            <div className="col-span-4">Product name</div>
            <div className="col-span-3">Brand (recommended)</div>
            <div className="col-span-2">UPC (optional)</div>
            <div className="col-span-2">Link (optional)</div>
            <div className="col-span-1" />
          </div>

          <div className="mt-1 space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <input
                  className="col-span-12 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none sm:col-span-4"
                  placeholder="Product name"
                  value={r.name}
                  disabled={submitting}
                  onChange={(e) => updateRow(i, { name: e.target.value })}
                />
                <input
                  className="col-span-12 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none sm:col-span-3"
                  placeholder="Brand"
                  value={r.brand}
                  disabled={submitting}
                  onChange={(e) => updateRow(i, { brand: e.target.value })}
                />
                <input
                  className="col-span-6 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none sm:col-span-2"
                  placeholder="UPC"
                  value={r.upc}
                  disabled={submitting}
                  onChange={(e) => updateRow(i, { upc: e.target.value })}
                />
                <input
                  className="col-span-5 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none sm:col-span-2"
                  placeholder="Link"
                  value={r.url}
                  disabled={submitting}
                  onChange={(e) => updateRow(i, { url: e.target.value })}
                />
                <button
                  type="button"
                  className="col-span-1 rounded-md text-gray-400 hover:text-red-600 disabled:opacity-40"
                  disabled={submitting || rows.length === 1}
                  onClick={() =>
                    setRows((rs) => rs.filter((_, idx) => idx !== i))
                  }
                  aria-label="Remove row"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div className="mt-3">
            <SecondaryButton
              type="button"
              onClick={() => setRows((rs) => [...rs, emptyRow()])}
              disabled={submitting}
            >
              + Add row
            </SecondaryButton>
          </div>
        </div>
      )}

      {/* Sticky action bar: the count, the rough cost and the one button that
          starts research stay in view while the list is being typed. */}
      <div className="sticky bottom-4 z-10 rounded-xl border-2 border-indigo-200 bg-white px-4 py-3 shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">
              {productCount > 0
                ? `${productCount} product${
                    productCount === 1 ? "" : "s"
                  } ready to research`
                : "Add at least one product above"}
            </div>
            {tokensPerProduct > 0 && (
              <div className="mt-0.5 text-sm text-gray-600">
                Rough cost:{" "}
                <span className="font-semibold text-gray-900">
                  {productCount > 0
                    ? `≈ ${estimate.toLocaleString()} tokens`
                    : `about ${Math.round(
                        tokensPerProduct
                      ).toLocaleString()} tokens per product`}
                </span>
                {productCount > 0 &&
                  ` (about ${Math.round(
                    tokensPerProduct
                  ).toLocaleString()} each)`}
                . You&apos;re only charged for what the run uses.
                {isStaff ? " Staff runs aren't balance-checked." : ""}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={run}
            disabled={!canRun}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-indigo-600 px-6 py-3 text-base font-bold text-white shadow-md transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300 disabled:shadow-none"
          >
            {submitting ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                Starting…
              </>
            ) : (
              `Research ${
                productCount > 1 ? `${productCount} products` : "product"
              } →`
            )}
          </button>
        </div>
        {!ingestChainId && (
          <p className="mt-2 text-sm text-amber-700">
            Product research isn&apos;t available right now.
          </p>
        )}
        {atCapacity && ingestChainId && (
          <p className="mt-2 text-sm text-amber-700">
            You have {MAX_CONCURRENT_JOBS} runs going — wait for one to finish
            (or clear it) in the runs panel before starting another.
          </p>
        )}
        {productCount > 0 && ingestChainId && !atCapacity && (
          <p className="mt-2 text-xs text-gray-500">
            Research runs in the background (usually a few minutes). We&apos;ll
            show you the results as soon as it&apos;s done.
          </p>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {error}
        </div>
      )}
      {outOfTokens && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          You&apos;re out of tokens for this billing period, so research
          can&apos;t start. Nothing was charged.{" "}
          <a
            href={TOP_UP_HREF}
            className="font-semibold text-indigo-700 underline hover:text-indigo-900"
          >
            Top up your balance
          </a>{" "}
          and try again.
        </div>
      )}
      {startedNote && !error && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {startedNote}
        </div>
      )}

      {/* Prominent failure dialog (replaces the old little red note). */}
      <Modal
        open={!!runError}
        tone="error"
        title="We couldn't finish researching"
        onClose={() => setRunError("")}
        footer={
          <>
            <SecondaryButton type="button" onClick={() => setRunError("")}>
              Close
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={() => {
                setRunError("");
                void run();
              }}
            >
              Try again
            </PrimaryButton>
          </>
        }
      >
        <p className="text-sm text-gray-700">
          Something went wrong while researching your products. Nothing was
          saved to the catalog.
        </p>
        <div className="mt-3 rounded-md border border-red-100 bg-red-50 p-3 font-mono text-xs text-red-700">
          {runError}
        </div>
        <p className="mt-3 text-xs text-gray-500">
          This is often a temporary hiccup with the research step. Trying again
          usually works. If it keeps failing, reduce the number of products or
          reach out to the team.
        </p>
      </Modal>
    </div>
  );
};

export default IngestTab;
